/**
 * Ce qui est programmé, et le rattrapage — à la ligne ou par paquet.
 *
 * > « Pour la modification de plusieurs stages en même temps, que ce soit le
 * >   prix, l'heure de début, etc., il faudrait un système de lignes cochées
 * >   puis modifier plusieurs sessions. Comme ça si j'ai envie de changer
 * >   toutes les dates/heures de lancement pour toutes mes sessions d'automne,
 * >   je ne doive pas le faire ligne par ligne. »
 *
 * Le geste programme la généralité ; cet écran rattrape — une ligne ou
 * quarante-cinq, par le même chemin.
 *
 * ## « Modifier » se coche, champ par champ
 *
 * Une correction groupée qui écrirait TOUS les champs du formulaire écraserait
 * les tarifs en voulant corriger l'heure. Chaque bloc porte donc sa case
 * « modifier », et seuls les blocs cochés partent. C'est exactement ce que les
 * drapeaux `p_changer_*` expriment en base.
 *
 * Et quand les lignes cochées ne s'accordent pas sur une valeur, le champ le
 * dit — « valeurs différentes » — au lieu d'en afficher une au hasard que le
 * premier enregistrement imposerait aux autres.
 *
 * ## L'heure se choisit dans une liste
 *
 * > « On ne lancera jamais de stages à 9:53, ce sera toujours des heures
 * >   rondes. »
 *
 * Le champ libre a produit en production quatre heures d'ouverture différentes
 * sur 45 sessions d'automne, dont une à 21:17. Une liste fermée supprime la
 * classe d'erreur. Le jour vide veut dire « visible dès la publication ».
 *
 * ## Les dates ne sont pas modifiables ici
 *
 * Elles viennent de la semaine déclarée. Les changer sur une session les
 * ferait diverger des autres sessions de la même semaine — le défaut exact que
 * ce chantier existe pour fermer. Pour déplacer une semaine, on déplace la
 * semaine.
 *
 * ## Les inscrits sont COMPTÉS
 *
 * `programme_sessions` les compte, elle ne lit pas `current_registrations` :
 * 14 des 82 sessions actives portaient ce compteur faux en septembre 2026,
 * jusqu'à 4 d'écart, parce que l'outil de transfert de groupe l'écrit à la main
 * depuis la copie du navigateur.
 */

import { useEffect, useMemo, useState } from 'react';
import { Archive, Check, Clock, Loader2, Pencil, Users, X } from 'lucide-react';
import clsx from 'clsx';
import {
  corriger,
  type Correction,
  type SessionProgrammee,
} from '../../../../lib/programme/gateway.supabase';
import {
  messageDErreur,
  nombreOuNull,
  phraseDeJours,
  prix,
} from '../../../../lib/programme/programmation';
import {
  assemblerHeureDeMur,
  decouperHeureDeMur,
  dejaOuvert,
  heuresProposees,
  phraseDOuverture,
  versHeureDeMur,
  versInstant,
} from '../../../../lib/programme/ouverture';

interface Condition {
  id: string;
  label: string;
}

interface Props {
  periodeLabel: string;
  annee: number;
  sessions: SessionProgrammee[];
  conditions: Condition[];
  onChangement: () => void;
  onErreur: (message: string) => void;
}

/** Quels blocs du formulaire partent réellement. */
interface Champs {
  ouverture: boolean;
  places: boolean;
  tarifs: boolean;
  condition: boolean;
}

interface Saisie {
  jour: string;
  heure: string;
  places: string;
  prix_normal: string;
  prix_reduit: string;
  prix_local: string;
  prix_local_reduit: string;
  tarif_condition_id: string;
}

const VIDE: Saisie = {
  jour: '',
  heure: '',
  places: '',
  prix_normal: '',
  prix_reduit: '',
  prix_local: '',
  prix_local_reduit: '',
  tarif_condition_id: '',
};

/**
 * La valeur si toutes les lignes s'accordent, `undefined` sinon.
 *
 * C'est ce qui permet au formulaire de dire « valeurs différentes » plutôt que
 * d'afficher celle de la première ligne — qui serait alors imposée à toutes les
 * autres au premier enregistrement.
 */
function valeurCommune<T>(
  lignes: SessionProgrammee[],
  lire: (s: SessionProgrammee) => T,
): T | undefined {
  if (lignes.length === 0) return undefined;
  const premiere = lire(lignes[0]);
  return lignes.every((s) => lire(s) === premiere) ? premiere : undefined;
}

const texte = (v: number | null | undefined) =>
  v === null || v === undefined ? '' : String(v);

const SessionsProgrammees = ({
  periodeLabel,
  annee,
  sessions,
  conditions,
  onChangement,
  onErreur,
}: Props) => {
  const [selection, setSelection] = useState<Set<string>>(new Set());
  /** Les sessions que la modale corrige. Vide = modale fermée. */
  const [aCorriger, setACorriger] = useState<string[]>([]);
  const [champs, setChamps] = useState<Champs>({
    ouverture: false,
    places: false,
    tarifs: false,
    condition: false,
  });
  const [saisie, setSaisie] = useState<Saisie>(VIDE);
  /** La session dont les places sont en cours de correction en place. */
  const [placesEnCours, setPlacesEnCours] = useState<{ id: string; valeur: string } | null>(null);
  const [envoi, setEnvoi] = useState(false);

  /*
    Une session disparue d'un rechargement ne doit pas rester cochée : le geste
    porterait sur un identifiant que la base refuserait, et refuserait TOUT le
    geste — c'est le comportement voulu de la fonction, autant ne pas le
    provoquer depuis l'écran.
  */
  useEffect(() => {
    const vivantes = new Set(sessions.map((s) => s.session_id));
    setSelection((courante) => {
      const nettoyee = new Set([...courante].filter((id) => vivantes.has(id)));
      return nettoyee.size === courante.size ? courante : nettoyee;
    });
  }, [sessions]);

  /* Regroupé par stage : c'est l'unité du geste, donc l'unité de la relecture. */
  const parStage = useMemo(() => {
    const groupes = new Map<string, { stage: string; lignes: SessionProgrammee[] }>();
    for (const s of sessions) {
      let g = groupes.get(s.stage_id);
      if (!g) {
        g = { stage: s.stage, lignes: [] };
        groupes.set(s.stage_id, g);
      }
      g.lignes.push(s);
    }
    return [...groupes.entries()].map(([stage_id, g]) => ({ stage_id, ...g }));
  }, [sessions]);

  const parId = useMemo(
    () => new Map(sessions.map((s) => [s.session_id, s])),
    [sessions],
  );

  const basculer = (id: string) =>
    setSelection((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const basculerGroupe = (lignes: SessionProgrammee[]) =>
    setSelection((s) => {
      const n = new Set(s);
      const toutes = lignes.every((l) => n.has(l.session_id));
      for (const l of lignes) {
        if (toutes) n.delete(l.session_id);
        else n.add(l.session_id);
      }
      return n;
    });

  /** Ouvre la modale sur un paquet, pré-remplie de ce que les lignes ont en commun. */
  const ouvrirCorrection = (ids: string[]) => {
    const lignes = ids.map((id) => parId.get(id)).filter(Boolean) as SessionProgrammee[];
    if (lignes.length === 0) return;

    const ouverture = valeurCommune(lignes, (s) => s.visible_from ?? '');
    const { jour, heure } =
      ouverture === undefined ? { jour: '', heure: '' } : decouperHeureDeMur(versHeureDeMur(ouverture));

    setSaisie({
      jour,
      heure,
      places: texte(valeurCommune(lignes, (s) => s.capacite)),
      prix_normal: texte(valeurCommune(lignes, (s) => s.prix_normal)),
      prix_reduit: texte(valeurCommune(lignes, (s) => s.prix_reduit)),
      prix_local: texte(valeurCommune(lignes, (s) => s.prix_local)),
      prix_local_reduit: texte(valeurCommune(lignes, (s) => s.prix_local_reduit)),
      tarif_condition_id: valeurCommune(lignes, (s) => s.tarif_condition_id ?? '') ?? '',
    });
    setChamps({ ouverture: false, places: false, tarifs: false, condition: false });
    setACorriger(ids);
  };

  const enregistrerPlaces = async (s: SessionProgrammee) => {
    const n = nombreOuNull(placesEnCours?.valeur ?? '');
    if (n === null || n < 1) {
      onErreur('Le nombre de places doit être au moins 1.');
      return;
    }
    setEnvoi(true);
    try {
      await corriger([s.session_id], { capacite: n });
      setPlacesEnCours(null);
      onChangement();
    } catch (err: unknown) {
      onErreur(messageDErreur(err, 'Les places n’ont pas pu être corrigées.'));
    } finally {
      setEnvoi(false);
    }
  };

  const enregistrerCorrection = async () => {
    if (aCorriger.length === 0) return;

    const correction: Correction = {};

    if (champs.ouverture) {
      /* Un jour vide veut dire « visible dès la publication » — donc NULL. */
      correction.visible_from = saisie.jour
        ? versInstant(assemblerHeureDeMur(saisie.jour, saisie.heure))
        : null;
    }

    if (champs.places) {
      const n = nombreOuNull(saisie.places);
      if (n === null || n < 1) {
        onErreur('Le nombre de places doit être au moins 1.');
        return;
      }
      correction.capacite = n;
    }

    if (champs.tarifs) {
      const normal = nombreOuNull(saisie.prix_normal);
      if (normal === null || normal < 0) {
        onErreur('Le tarif normal est obligatoire et ne peut pas être négatif.');
        return;
      }
      correction.tarifs = {
        prix_normal: normal,
        prix_reduit: nombreOuNull(saisie.prix_reduit),
        prix_local: nombreOuNull(saisie.prix_local),
        prix_local_reduit: nombreOuNull(saisie.prix_local_reduit),
      };
    }

    if (champs.condition) {
      correction.tarif_condition_id = saisie.tarif_condition_id || null;
    }

    if (Object.keys(correction).length === 0) {
      onErreur('Cocher au moins un champ à modifier.');
      return;
    }

    setEnvoi(true);
    try {
      /*
        Les trois inégalités d'un rabais et la comparaison aux inscrits ne sont
        PAS revérifiées ici : `programme_corriger` les tient, et son refus
        arrive avec la phrase à montrer. Les dupliquer, c'était laisser deux
        vérités diverger.
      */
      const corrigees = await corriger(aCorriger, correction);
      setACorriger([]);
      setSelection(new Set());
      onChangement();
      if (corrigees.length > 1) {
        onErreur(`${corrigees.length} sessions corrigées.`);
      }
    } catch (err: unknown) {
      onErreur(messageDErreur(err, 'La correction n’a pas pu être enregistrée.'));
    } finally {
      setEnvoi(false);
    }
  };

  if (sessions.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <Users className="h-5 w-5 text-primary-600" />
          Ce qui est programmé
        </h2>
        <div className="text-center py-8 text-gray-500 text-sm border-2 border-dashed border-gray-200 rounded-lg">
          Rien encore pour {periodeLabel} {annee}.
        </div>
      </div>
    );
  }

  const lignesDeLaModale = aCorriger
    .map((id) => parId.get(id))
    .filter(Boolean) as SessionProgrammee[];
  const rienCoche = !champs.ouverture && !champs.places && !champs.tarifs && !champs.condition;

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="mb-4">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Users className="h-5 w-5 text-primary-600" />
          Ce qui est programmé — {periodeLabel} {annee}
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          {sessions.length} session{sessions.length > 1 ? 's' : ''}. Cochez des lignes pour
          les corriger ensemble ; les dates viennent de la semaine et se changent sur la
          semaine.
        </p>
      </div>

      {/* --- La barre de sélection --- */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <button
          onClick={() =>
            setSelection(
              selection.size === sessions.length
                ? new Set()
                : new Set(sessions.map((s) => s.session_id)),
            )
          }
          className="btn-outline text-sm py-1.5"
        >
          {selection.size === sessions.length
            ? 'Tout décocher'
            : `Tout cocher (${sessions.length})`}
        </button>

        {selection.size > 0 && (
          <>
            <span className="text-sm text-gray-600">
              {selection.size} session{selection.size > 1 ? 's' : ''} cochée
              {selection.size > 1 ? 's' : ''}
            </span>
            <button
              onClick={() => ouvrirCorrection([...selection])}
              className="btn-primary text-sm py-1.5 flex items-center gap-1.5"
            >
              <Pencil className="h-3.5 w-3.5" />
              Modifier {selection.size} session{selection.size > 1 ? 's' : ''}
            </button>
          </>
        )}
      </div>

      <div className="space-y-6">
        {parStage.map((groupe) => {
          const toutCoche = groupe.lignes.every((l) => selection.has(l.session_id));
          return (
            <div key={groupe.stage_id}>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {groupe.stage}
                <span className="font-normal text-gray-500">
                  {' '}
                  — {groupe.lignes.length} session{groupe.lignes.length > 1 ? 's' : ''}
                </span>
              </h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 w-8">
                        <input
                          type="checkbox"
                          checked={toutCoche}
                          onChange={() => basculerGroupe(groupe.lignes)}
                          className="rounded border-gray-300"
                          title={`Cocher les ${groupe.lignes.length} sessions de ce stage`}
                        />
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Semaine
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Centre
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Tarifs
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Inscrits
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                        Places
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Ouverture
                      </th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {groupe.lignes.map((s) => {
                      const ecourtee = s.jours_ouverts < s.jours_reference;
                      const complet = s.inscrits >= s.capacite;
                      const localSansCondition =
                        s.prix_local !== null && s.tarif_condition_id === null;
                      const enEdition = placesEnCours?.id === s.session_id;
                      const cochee = selection.has(s.session_id);
                      const ouverte = dejaOuvert(s.visible_from);
                      return (
                        <tr
                          key={s.session_id}
                          className={clsx(
                            'hover:bg-gray-50',
                            s.archived && 'bg-gray-50',
                            cochee && 'bg-primary-50 hover:bg-primary-50',
                          )}
                        >
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={cochee}
                              onChange={() => basculer(s.session_id)}
                              className="rounded border-gray-300"
                            />
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="font-medium text-gray-900">{s.semaine}</span>
                            {ecourtee && (
                              <span className="ml-1.5 text-xs text-amber-700">
                                {phraseDeJours(s.jours_ouverts, s.jours_reference)}
                              </span>
                            )}
                            {s.archived && (
                              <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-700">
                                <Archive className="h-3 w-3" />
                                archivée
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                            {s.centre}
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap">
                            <span className="text-gray-900">{prix(s.prix_normal)}</span>
                            {s.prix_reduit !== null && (
                              <span className="text-gray-500"> / {prix(s.prix_reduit)}</span>
                            )}
                            {s.prix_local !== null && (
                              <span
                                className={
                                  localSansCondition ? 'text-amber-700' : 'text-gray-500'
                                }
                                title={
                                  localSansCondition
                                    ? 'Tarif local sans condition : il ne s’applique à personne'
                                    : (s.tarif_condition ?? undefined)
                                }
                              >
                                {' '}
                                · local {prix(s.prix_local)}
                                {localSansCondition && ' ⚠'}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <span
                              className={clsx(
                                'font-medium',
                                complet ? 'text-red-600' : 'text-gray-700',
                              )}
                            >
                              {s.inscrits}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right">
                            {enEdition ? (
                              <div className="flex items-center gap-1 justify-end">
                                <input
                                  type="number"
                                  min={Math.max(1, s.inscrits)}
                                  value={placesEnCours.valeur}
                                  onChange={(e) =>
                                    setPlacesEnCours({ id: s.session_id, valeur: e.target.value })
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') enregistrerPlaces(s);
                                    if (e.key === 'Escape') setPlacesEnCours(null);
                                  }}
                                  className="w-16 px-2 py-1 border border-primary-400 rounded text-right text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                                  autoFocus
                                />
                                <button
                                  onClick={() => enregistrerPlaces(s)}
                                  className="text-green-600 hover:text-green-800"
                                  title="Enregistrer"
                                  disabled={envoi}
                                >
                                  {envoi ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  onClick={() => setPlacesEnCours(null)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Annuler"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() =>
                                  setPlacesEnCours({
                                    id: s.session_id,
                                    valeur: String(s.capacite),
                                  })
                                }
                                className="px-2 py-0.5 rounded hover:bg-primary-100 hover:text-primary-700 tabular-nums"
                                title="Corriger les places"
                              >
                                {s.capacite}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs max-w-[190px]">
                            <span
                              className={clsx(
                                'truncate block',
                                ouverte ? 'text-green-700' : 'text-gray-500',
                              )}
                              title={phraseDOuverture(s.visible_from)}
                            >
                              {s.visible_from
                                ? phraseDOuverture(s.visible_from).replace('À partir du ', '')
                                : 'immédiate'}
                            </span>
                            {ouverte && (
                              <span className="inline-flex items-center gap-1 text-[11px] text-green-700 font-medium">
                                <Clock className="h-3 w-3" />
                                déjà ouverte
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <button
                              onClick={() => ouvrirCorrection([s.session_id])}
                              className="text-primary-600 hover:text-primary-900"
                              title="Corriger cette session"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}
      </div>

      {/* --- La correction, d'une session ou de quarante-cinq --- */}
      {lignesDeLaModale.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 my-8">
            <h3 className="text-lg font-semibold mb-1">
              Corriger {lignesDeLaModale.length} session
              {lignesDeLaModale.length > 1 ? 's' : ''}
            </h3>
            <p className="text-sm text-gray-600 mb-5">
              Seuls les blocs cochés sont écrits. Les autres restent tels quels sur chaque
              session.
            </p>

            {/* Ouverture */}
            <BlocCoche
              titre="Heure d’ouverture des inscriptions"
              coche={champs.ouverture}
              onCoche={(v) => setChamps({ ...champs, ouverture: v })}
            >
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="form-label">Jour</label>
                  <input
                    type="date"
                    value={saisie.jour}
                    onChange={(e) => setSaisie({ ...saisie, jour: e.target.value })}
                    className="form-input"
                  />
                </div>
                <div>
                  <label className="form-label">Heure (Bruxelles)</label>
                  <select
                    value={saisie.heure}
                    onChange={(e) => setSaisie({ ...saisie, heure: e.target.value })}
                    className="form-input"
                    disabled={!saisie.jour}
                  >
                    {heuresProposees(saisie.heure).map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {saisie.jour
                  ? phraseDOuverture(versInstant(assemblerHeureDeMur(saisie.jour, saisie.heure)))
                  : 'Jour vide : les sessions deviennent visibles immédiatement.'}
              </p>
            </BlocCoche>

            {/* Places */}
            <BlocCoche
              titre="Nombre de places"
              coche={champs.places}
              onCoche={(v) => setChamps({ ...champs, places: v })}
            >
              <input
                type="number"
                min="1"
                value={saisie.places}
                onChange={(e) => setSaisie({ ...saisie, places: e.target.value })}
                className="form-input"
                placeholder="valeurs différentes"
              />
            </BlocCoche>

            {/* Tarifs */}
            <BlocCoche
              titre="Tarifs"
              coche={champs.tarifs}
              onCoche={(v) => setChamps({ ...champs, tarifs: v })}
            >
              <div className="grid grid-cols-2 gap-3">
                {(
                  [
                    ['prix_normal', 'Normal', true],
                    ['prix_reduit', 'Réduit', false],
                    ['prix_local', 'Local', false],
                    ['prix_local_reduit', 'Local réduit', false],
                  ] as const
                ).map(([champ, libelle, obligatoire]) => (
                  <div key={champ}>
                    <label className="form-label">
                      {libelle}
                      {obligatoire && <span className="text-red-500"> *</span>}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={saisie[champ]}
                      onChange={(e) => setSaisie({ ...saisie, [champ]: e.target.value })}
                      className="form-input"
                      placeholder={obligatoire ? 'obligatoire' : '—'}
                    />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Le tarif est écrit tel quel — il n’est pas proratisé, puisqu’il n’est pas
                repris d’un geste.
              </p>
            </BlocCoche>

            {/* Condition */}
            <BlocCoche
              titre="Condition tarifaire"
              coche={champs.condition}
              onCoche={(v) => setChamps({ ...champs, condition: v })}
            >
              <select
                value={saisie.tarif_condition_id}
                onChange={(e) => setSaisie({ ...saisie, tarif_condition_id: e.target.value })}
                className="form-input"
              >
                <option value="">Aucune</option>
                {conditions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </BlocCoche>

            <p className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4">
              Les factures déjà émises ne changent pas : le montant d’une inscription est
              figé à la commande.
            </p>

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setACorriger([])} className="btn-outline">
                Annuler
              </button>
              <button
                onClick={enregistrerCorrection}
                className="btn-primary flex items-center"
                disabled={envoi || rienCoche}
                title={rienCoche ? 'Cocher au moins un bloc à modifier' : undefined}
              >
                {envoi && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/** Un bloc du formulaire, inerte tant que sa case n'est pas cochée. */
const BlocCoche = ({
  titre,
  coche,
  onCoche,
  children,
}: {
  titre: string;
  coche: boolean;
  onCoche: (v: boolean) => void;
  children: React.ReactNode;
}) => (
  <div
    className={clsx(
      'border rounded-lg p-3 mb-3 transition-colors',
      coche ? 'border-primary-300 bg-primary-50/40' : 'border-gray-200',
    )}
  >
    <label className="flex items-center gap-2 cursor-pointer">
      <input
        type="checkbox"
        checked={coche}
        onChange={(e) => onCoche(e.target.checked)}
        className="rounded border-gray-300"
      />
      <span className="text-sm font-medium text-gray-900">{titre}</span>
    </label>
    <div className={clsx('mt-3', !coche && 'opacity-40 pointer-events-none')}>{children}</div>
  </div>
);

export default SessionsProgrammees;
